import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MACRON_URL = 'https://www.macron.com/wo/';

export default function MacronPage() {
  const { t } = useLanguage();
  const m = t.macron;

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="macron-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden" data-testid="macron-hero">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #CE2B37 0%, transparent 40%)' }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-dm text-xs uppercase tracking-[3px] text-[#CE2B37] mb-6"
            data-testid="macron-eyebrow"
          >
            {m.eyebrow}
          </motion.p>

          <motion.a
            href={MACRON_URL}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-block group"
            data-testid="macron-hero-logo"
          >
            <img
              src="/macron/macron-white.png"
              alt={m.logoAlt}
              className="h-14 md:h-20 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </motion.a>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 max-w-2xl font-dm text-base md:text-lg text-white/70 leading-relaxed"
            data-testid="macron-intro"
          >
            {m.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={MACRON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#CE2B37] text-white font-bebas text-lg tracking-wider px-8 py-4 hover:bg-[#a82230] transition-colors duration-200"
              data-testid="macron-visit-cta"
            >
              {m.visitCta} <ExternalLink size={16} />
            </a>
            <Link
              to="/contact?message=Macron%20product%20enquiry"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bebas text-lg tracking-wider px-8 py-4 hover:border-white hover:bg-white/5 transition-colors duration-200"
              data-testid="macron-quote-cta"
            >
              {m.quoteCta} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Collection */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]" data-testid="macron-collection">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h2 className="font-bebas text-3xl md:text-4xl text-[#0D0D0D] tracking-tight mb-10" data-testid="macron-collection-title">
            {m.collectionTitle}
          </h2>

          <div className="border border-dashed border-[#0D0D0D]/20 rounded-lg py-16 px-6 text-center bg-white" data-testid="macron-coming-soon">
            <img src="/macron/macron-black.png" alt={m.logoAlt} className="h-8 w-auto mx-auto opacity-60 mb-6" />
            <p className="font-bebas text-2xl text-[#0D0D0D] tracking-wide mb-3">{m.comingSoon}</p>
            <p className="font-dm text-sm md:text-base text-[#0D0D0D]/60 max-w-md mx-auto leading-relaxed">
              {m.comingSoonBody}
            </p>
            <Link
              to="/contact?message=Macron%20product%20enquiry"
              className="mt-8 inline-flex items-center gap-2 font-dm text-sm uppercase tracking-wider text-[#CE2B37] hover:gap-3 transition-all duration-200"
              data-testid="macron-coming-soon-cta"
            >
              {m.quoteCta} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
